import type { Draft } from "@reduxjs/toolkit";

import type { NodeReferences, ReplaceNodeAction } from "../../schemas/index.ts";
import { repointNodeValueRefs } from "../../shared/index.ts";
import type { NodeDesignsState } from "../types.ts";

/**
 * A replacement stands in for the node it replaces only when the action says so:
 * then everything that pointed at the old node is repointed at the newcomer.
 * Otherwise the references clear as they would on removal.
 */
export const replacementRepointsReferences = ({ repointRefs }: ReplaceNodeAction["payload"]): boolean => !!repointRefs;

/**
 * Rewrites every reference to node `from` so it names node `to` instead: node configs
 * (`nodeReferences`, configured values), and the buildables and trigger cases held in
 * trigger designs (source and target node ids, `refs`, configured values). The old
 * node's own triggers are not repointed: the newcomer brings its own, and removal
 * takes the old ones with it.
 */
export const repointNodeReferences = (state: Draft<NodeDesignsState>, from: number, to: number) => {
  const repointId = (id: number) => (id === from ? to : id);

  const repointRefs = (refs: Draft<NodeReferences> | undefined) => {
    if (!refs) return;
    for (const [name, idOrIds] of Object.entries(refs)) {
      if (Array.isArray(idOrIds)) {
        refs[name] = idOrIds.map(repointId);
      } else if (idOrIds === from) {
        refs[name] = to;
      }
    }
  };

  const { designsById, triggerDesigns } = state;

  for (const { config } of Object.values(designsById)) {
    repointRefs(config.nodeReferences);
    if (config.values) {
      config.values = repointNodeValueRefs(config.values, repointId);
    }
  }

  for (const effect of Object.values(triggerDesigns.effects.byId)) {
    effect.nodeId = repointId(effect.nodeId);
    if (effect.targetNodeId !== undefined) {
      effect.targetNodeId = repointId(effect.targetNodeId);
    }
    effect.config = repointNodeValueRefs(effect.config, repointId);
    repointRefs(effect.config.refs);
  }

  for (const condition of Object.values(triggerDesigns.conditions.byId)) {
    if (condition.nodeId !== undefined) {
      condition.nodeId = repointId(condition.nodeId);
    }
    condition.config = repointNodeValueRefs(condition.config, repointId);
    repointRefs(condition.config.refs);
  }

  const cased = [...Object.values(triggerDesigns.triggersById), ...Object.values(triggerDesigns.handlersById)];
  for (const { cases } of cased) {
    for (const trigger of cases ?? []) {
      if (!trigger.where) continue;
      trigger.where.config = repointNodeValueRefs(trigger.where.config, repointId);
      repointRefs(trigger.where.config.refs);
    }
  }
};
